import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/apiClient'
import { goodsReceivedReportsQueryKey } from '@/features/cbam-reporting/hooks/importedGoods'
import type { BatchLiabilityResponse } from '@/features/cbam-reporting/hooks/cbamLiability'

export enum ImportedGoodsGRRFieldNames {
  MRN_NO = 'MRN No.',
  DATE = 'Date',
  HS_CODE = 'HS Code',
  COO = 'Country of Origin',
  NETT = 'Net Weight',
  UNIQUE_IMPORT_ID_CBAM = 'Unique Import ID CBAM',
  CUSTOMS_RULE = 'Customs Rule',
  SELLER = 'Seller',
  DESCRIPTION = 'Description of the goods'
}

// Field mappings for the imported goods GRR
export const importedGoodsGRRFields: Record<string, ImportedGoodsGRRFieldNames> = {
  mrnNo: ImportedGoodsGRRFieldNames.MRN_NO,
  date: ImportedGoodsGRRFieldNames.DATE,
  hsCode: ImportedGoodsGRRFieldNames.HS_CODE,
  coo: ImportedGoodsGRRFieldNames.COO,
  nett: ImportedGoodsGRRFieldNames.NETT,
  uniqueImportIdCbam: ImportedGoodsGRRFieldNames.UNIQUE_IMPORT_ID_CBAM,
  customsRule: ImportedGoodsGRRFieldNames.CUSTOMS_RULE
}

// Error configuration
export const importedGoodsGRRErrors: Record<string, { isBlockingEvaluation: boolean }> = {
  // CSV File Error
  MISSING_REQUIRED_COLUMN: { isBlockingEvaluation: true },
  EMPTY_FILE: { isBlockingEvaluation: true },
  // Date Validation Errors
  DATE_MISSING: { isBlockingEvaluation: false },
  DATE_INVALID_FORMAT: { isBlockingEvaluation: false },
  DATE_OUTSIDE_PERIOD: { isBlockingEvaluation: false },
  // CN Code Validation Errors
  CN_CODE_MISSING: { isBlockingEvaluation: false },
  CN_CODE_INVALID_FORMAT: { isBlockingEvaluation: true },
  CN_CODE_NOT_CBAM_COMPLIANT: { isBlockingEvaluation: false },
  // Net Weight Validation Errors
  NET_WEIGHT_MISSING: { isBlockingEvaluation: false },
  NET_WEIGHT_ZERO: { isBlockingEvaluation: false },
  // Country Code Validation Errors
  COUNTRY_CODE_MISSING: { isBlockingEvaluation: true },
  COUNTRY_CODE_TOO_SHORT: { isBlockingEvaluation: true },
  COUNTRY_CODE_TOO_LONG: { isBlockingEvaluation: true },
  COUNTRY_CODE_INVALID_ISO: { isBlockingEvaluation: true },
  COUNTRY_CODE_EU: { isBlockingEvaluation: false },
  // Custom ID Errors
  CUSTOM_ID_DUPLICATE_PREVIOUS_UPLOAD: { isBlockingEvaluation: true },
  CUSTOM_ID_DUPLICATE_DIFFERENT_DATE: { isBlockingEvaluation: true },
  IMPORTER_NOT_FOUND: { isBlockingEvaluation: false },
  INVALID_CUSTOMS_PROCEDURE: { isBlockingEvaluation: false }
}

export type ValidatedImportedGoodRow = {
  customId?: string
  importDate: string
  cnCode: string
  description?: string
  sellerName?: string
  country: string
  netMassInTonnes: number
  importerIdentificationNumber?: string
  importerId?: string
  importerName?: string
  requestedCustomsProcedure?: string
}

export type ImportedGoodsGRRProcessResult = {
  periodId: string
  data: ValidatedImportedGoodRow[]
  goodsReceivedReportId: string | null
  liabilityPreview: BatchLiabilityResponse | null
  errors: { key: string; row: number; field: string; value: any }[]
  stats: {
    totalRows: number
    validRows: number
    errorRows: number
  }
}

type FileFormat = 'csv' | 'xlsx'

const detectFileFormat = (file: File): FileFormat => {
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
  return fileExtension === '.xlsx' ? 'xlsx' : 'csv'
}

const convertFileContent = async (file: File, format: FileFormat): Promise<string> => {
  if (format === 'xlsx') {
    // Convert File to base64 for Excel files
    const arrayBuffer = await file.arrayBuffer()
    return btoa(
      new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    )
  }
  // Read as text for CSV files (existing behavior)
  return await file.text()
}

export const useProcessImportedGoodsGRR = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      file,
      fileFormat
    }: {
      file: File
      fileFormat: FileFormat
    }): Promise<ImportedGoodsGRRProcessResult> => {
      // Detect file format from extension
      const detectedFormat = detectFileFormat(file)

      // Validate detected format matches provided format
      if (detectedFormat !== fileFormat) {
        throw new Error(`File format mismatch: detected ${detectedFormat}, expected ${fileFormat}`)
      }

      // Read file content
      const fileContent = await convertFileContent(file, fileFormat)

      const response = await apiClient.post<ImportedGoodsGRRProcessResult>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/imported-goods/grr-process`,
        {
          file: fileContent,
          fileFormat: detectedFormat,
          fileName: file.name
        },
        {
          timeout: 300000 // 5 minutes — bulk operations can exceed default client timeout
        }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: goodsReceivedReportsQueryKey(organizationId, cbamReportId)
      })
    }
  })
}
